/**
 * Shop Store - Zustand state management for shop data
 */

import { create } from 'zustand';
import { Shop } from '../types';
import { createLogger } from '../config/environment';

const logger = createLogger('ShopStore');

interface ShopState {
  shop: Shop | null;
  isLoading: boolean;
  error: string | null;
}

interface ShopActions {
  setShop: (shop: Shop) => void;
  updateShop: (shop: Partial<Shop>) => void;
  clearShop: () => void;
  setError: (error: string) => void;
  resetError: () => void;
}

export type ShopStore = ShopState & ShopActions;

/**
 * Create shop store with Zustand
 */
export const useShopStore = create<ShopStore>((set) => ({
  // Initial state
  shop: null,
  isLoading: false,
  error: null,

  // Set shop
  setShop: (shop: Shop) => {
    logger.info(`Setting shop: ${shop.id}`);
    set({ shop });
  },

  // Update shop (merge with existing)
  updateShop: (shopUpdate: Partial<Shop>) => {
    set((state) => {
      if (!state.shop) {
        logger.warn('Cannot update shop - no shop set');
        return state;
      }
      return {
        shop: { ...state.shop, ...shopUpdate },
      };
    });
  },

  // Clear shop
  clearShop: () => {
    logger.info('Clearing shop');
    set({ shop: null });
  },

  // Set error
  setError: (error: string) => {
    logger.error('Shop error:', error);
    set({ error });
  },

  // Reset error
  resetError: () => {
    set({ error: null });
  },
}));

/**
 * Helper hook to get current shop
 */
export const useShop = (): Shop | null => {
  return useShopStore((state) => state.shop);
};

/**
 * Helper hook to get shop ID
 */
export const useShopId = (): string | null => {
  return useShopStore((state) => state.shop?.id || null);
};

/**
 * Helper hook to get shop name
 */
export const useShopName = (): string | null => {
  return useShopStore((state) => state.shop?.name || null);
};

